/**
 * Library attach-vse 완료 대기 (Main API → SSE viewr_update_complete).
 */

import { apiService } from "../apiService";
import { getFreshToken } from "../api/base";
import { sseService } from "../sseService";

const DEFAULT_ATTACH_VSE_TIMEOUT_MS = 120_000;

export async function prepareLibraryAttachViewrUpdateSse(
  jobId: string,
): Promise<void> {
  const token = await getFreshToken();
  if (!token) {
    throw new Error("library_attach_auth_required");
  }

  sseService.clearJobId("viewr_update");
  sseService.setJobId("viewr_update", jobId);

  if (sseService.isConnected("viewr_update")) {
    return;
  }

  sseService.connect(
    "viewr_update",
    token,
    (data) => {
      if (
        data.type === "viewr_update_complete" ||
        data.type === "viewr_update_completed"
      ) {
        window.dispatchEvent(
          new CustomEvent("viewr:updateComplete", { detail: data }),
        );
      }
    },
    (error) => {
      console.warn("[library-attach] viewr_update SSE error:", error);
    },
    null,
    { owner: "library_attach" },
  );

  await sseService.waitForOpen("viewr_update", 10_000).catch(() => undefined);
}

type ViewrUpdateCompleteDetail = {
  type?: string;
  job_id?: string;
  jobId?: string;
  result?: {
    status?: string;
    gcs_path?: string;
    error?: string;
  };
};

export function waitForLibraryAttachVseComplete(
  jobId: string,
  timeoutMs: number = DEFAULT_ATTACH_VSE_TIMEOUT_MS,
): Promise<{ gcsPath: string }> {
  const normalizedJobId = jobId.trim();
  if (!normalizedJobId) {
    return Promise.reject(new Error("library_attach_job_id_required"));
  }

  return new Promise((resolve, reject) => {
    let disposed = false;
    let polling = false;
    const poll = async () => {
      if (disposed || polling) return;
      polling = true;
      try {
        const token = await getFreshToken();
        if (disposed) return;
        const job = await apiService.getConversionJobStatus(normalizedJobId, token) as { job_id?: string; status?: string; viewr_gcs_path?: string; error_message?: string } | null;
        if (disposed || !job || (job.job_id && job.job_id !== normalizedJobId)) return;
        if (["failed", "cancelled", "attach_vse_failed"].includes(job.status ?? "")) {
          cleanup(); reject(new Error(job.error_message || "library_attach_failed"));
        } else if (["completed", "success", "attach_vse_completed"].includes(job.status ?? "") && job.viewr_gcs_path) {
          cleanup(); resolve({ gcsPath: job.viewr_gcs_path });
        }
      } catch { /* unknown readback retains the same Job */ }
      finally { polling = false; }
    };
    const pollTimer = window.setInterval(() => { void poll(); }, 2000);
    const timeoutId = window.setTimeout(() => {
      cleanup();
      reject(new Error("library_attach_viewr_update_timeout"));
    }, timeoutMs);

    const onUpdateComplete = (event: Event) => {
      const detail = (event as CustomEvent<ViewrUpdateCompleteDetail>).detail ?? {};
      const eventJobId = (detail.job_id || detail.jobId || "").trim();
      if (eventJobId !== normalizedJobId) {
        return;
      }

      const result = detail.result ?? {};
      if (result.status === "failed") {
        cleanup();
        reject(new Error(result.error || "library_attach_failed"));
        return;
      }

      const gcsPath = (result.gcs_path || "").trim();
      if (!gcsPath) {
        cleanup();
        reject(new Error("library_attach_missing_gcs_path"));
        return;
      }

      cleanup();
      resolve({ gcsPath });
    };

    const cleanup = () => {
      disposed = true;
      window.clearInterval(pollTimer);
      window.clearTimeout(timeoutId);
      window.removeEventListener("viewr:updateComplete", onUpdateComplete as EventListener);
    };

    window.addEventListener("viewr:updateComplete", onUpdateComplete as EventListener);
    void poll();
  });
}
